import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isApiError } from '@/lib/apiError';
import { protocolosKeys } from '@/features/protocolos/hooks/useProtocolos';

import { informesApi } from '../api';

const QUERY_KEY_BASE = ['informes'] as const;

export const informesKeys = {
  all: QUERY_KEY_BASE,
  parcial: (uidProtocolo: string) => [...QUERY_KEY_BASE, 'parcial', uidProtocolo] as const,
  final: (uidProtocolo: string) => [...QUERY_KEY_BASE, 'final', uidProtocolo] as const,
};

/**
 * Por default `enabled: false` — el componente dispara con refetch()
 * cuando el usuario hace click en "Ver informe parcial".
 */
export function useInformeParcial(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo ? informesKeys.parcial(uidProtocolo) : informesKeys.all,
    queryFn: ({ signal }) => informesApi.getParcial(uidProtocolo!, signal),
    enabled: false,
  });
}

export function useInformeFinal(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo ? informesKeys.final(uidProtocolo) : informesKeys.all,
    queryFn: ({ signal }) => informesApi.getFinal(uidProtocolo!, signal),
    enabled: false,
    retry: (failureCount, error) => {
      if (isApiError(error) && error.isNotFound()) return false;
      return failureCount < 1;
    },
  });
}

/**
 * Después del éxito, invalida el protocolo (informe_final_emitido cambió a true).
 */
export function useEmitirInformeFinal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uidProtocolo: string) => informesApi.emitirFinal(uidProtocolo),
    onSuccess: (_data, uidProtocolo) => {
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.detail(uidProtocolo) });
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.all });
      void queryClient.invalidateQueries({ queryKey: informesKeys.final(uidProtocolo) });
    },
  });
}
